import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import PropTypes from "prop-types";
import { formatDate } from "../utils/formatTime";
import { useState } from "react";

ItemCardList.propTypes = {
    items: PropTypes.array,
    loadedItems: PropTypes.object,
    error: PropTypes.string,
    onItemClick: PropTypes.func.isRequired,
    emptyStateMessage: PropTypes.string,
    loadingMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    getItemName: PropTypes.func,
    getItemSubtitle: PropTypes.func,
    getItemChipLabel: PropTypes.func,
    getItemDate: PropTypes.func,
    showDate: PropTypes.bool,
    showChip: PropTypes.bool,
    customIcon: PropTypes.elementType,
};

ItemCardList.defaultProps = {
    items: null,
    loadedItems: null,
    error: null,
    emptyStateMessage: "No items available",
    loadingMessage: "Loading items...",
    errorMessage: "Failed to load items.",
    getItemName: (item) => item.name || `Item ${item.id}`,
    getItemSubtitle: (item) => null,
    getItemChipLabel: (item) => item.category || "Unknown",
    getItemDate: (item) => item.createdAt,
    showDate: true,
    showChip: true,
    customIcon: null,
};

export function ItemCardList({
    items,
    loadedItems,
    error,
    onItemClick,
    emptyStateMessage,
    loadingMessage,
    errorMessage,
    getItemName,
    getItemSubtitle,
    getItemChipLabel,
    getItemDate,
    showDate,
    showChip,
    customIcon: CustomIcon,
}) {
    const [selectedItemId, setSelectedItemId] = useState(null);

    const handleItemClick = (item) => {
        setSelectedItemId(item.id);
        onItemClick(item);
    };
    return (
        <Box>
            {items && items.length > 0 ? (
                <Box sx={{ maxHeight: 350, minHeight: 120, overflow: "auto" }}>
                    {items.map((item) => (
                        <Card
                            key={item.id}
                            sx={{
                                mb: 2,
                                mx: 1,
                                border: selectedItemId === item.id ? 2 : 1,
                                borderColor:
                                    selectedItemId === item.id
                                        ? "primary.main"
                                        : "divider",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 3,
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Box flex={1}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mb={1}
                                        >
                                            <CustomIcon
                                                color="primary"
                                                fontSize="small"
                                            />
                                            <Tooltip
                                                title={getItemName(item)}
                                                disableHoverListener={
                                                    getItemName(item).length <=
                                                    30
                                                }
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="medium"
                                                    noWrap
                                                    sx={{ maxWidth: 250 }}
                                                >
                                                    {getItemName(item)}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                            mb={1}
                                        >
                                            {showChip && (
                                                <Chip
                                                    label={getItemChipLabel(
                                                        item
                                                    )}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                            {showDate && getItemDate(item) && (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <CalendarMonthIcon
                                                        fontSize="small"
                                                        color="action"
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {formatDate(
                                                            getItemDate(item)
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        {getItemSubtitle(item) && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 1 }}
                                            >
                                                {getItemSubtitle(item)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Tooltip
                                        title={
                                            loadedItems.has(item.id)
                                                ? `Select ${getItemName(
                                                      item
                                                  ).toLowerCase()}`
                                                : `Load ${getItemName(
                                                      item
                                                  ).toLowerCase()}`
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                onClick={() =>
                                                    handleItemClick(item)
                                                }
                                                disabled={false}
                                                sx={{
                                                    "&:hover": {
                                                        bgcolor:
                                                            loadedItems.has(
                                                                item.id
                                                            ) &&
                                                            "success.light",
                                                    },
                                                }}
                                            >
                                                {loadedItems.has(item.id) ? (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            color: "primary.main",
                                                        }}
                                                    />
                                                ) : (
                                                    <DownloadIcon />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : items && items.length === 0 ? (
                <Card sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
                    <CustomIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                    />
                    <Typography color="textSecondary" variant="body1">
                        {emptyStateMessage}
                    </Typography>
                </Card>
            ) : !error ? (
                <Card sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
                    <Box display="flex" justifyContent="center" mb={2}>
                        <CircularProgress />
                    </Box>
                    <Typography color="textSecondary" variant="body1">
                        {loadingMessage}
                    </Typography>
                </Card>
            ) : (
                <Card sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
                    <CustomIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                    />
                    <Typography color="textSecondary" variant="body1">
                        {errorMessage}
                    </Typography>
                </Card>
            )}
        </Box>
    );
}
